import { TriggerConfig } from "./emailConfig";

/**
 * Agent-specific email triggers configuration
 * These triggers are used for agent-related email notifications
 *
 * Email shell branding uses `getAuthCookie("userId")` for the marketplace `created-by` API
 * in `sendTriggerEmails` (same as all triggers).
 */
export const agentEmailTriggers: Record<string, TriggerConfig> = {
  "Agent Password Changed": {
    subject: " Your Realesy Password Has Been Updated",
    title: "Password Updated",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your password has been successfully updated. If this was you, no further action is needed.
        </p>
      `,
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Sales Order Placed": {
    subject: {
      AGENT: " Your Sales Order Has Been Placed – #{{Order ID}}",
      SIGNAGE_FIRM: " New Order Received from {{Agent Name}} – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " New Order Received from {{Agent Name}} – #{{Order ID}}",
    },
    title: "Order Placed",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage order has been successfully placed. Below are the details for your reference:
        </p>
        
        <h3 class="email-heading-h3">
           Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installation Address</td>
              <td class="email-order-details-value">{{Property or Office Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Install Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You will be notified as the order progresses through production, shipment, and installation.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          A new sales order has been placed by <strong>{{Agent Name}}</strong> and assigned to your company. Please review the details below:
        </p>
        
        <h3 class="email-heading-h3">
           Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{Install Date}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Be sure to update the production and installation status as you progress.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          A new sales order has been placed by <strong>{{Agent Name}}</strong> and assigned to your company. Please review the details below:
        </p>
        
        <h3 class="email-heading-h3">
           Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{Install Date}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Be sure to update the production and installation status as you progress.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Access Orders Queue", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Access Orders Queue", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Sales Order Shipped": {
    subject: " Your Signage Order Has Shipped – #{{Order ID}}",
    title: "Order Shipped",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage order #{{Order ID}} has been marked as shipped by {{Supplier Name}}. Here are the shipping details:
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped By</td>
              <td class="email-order-details-value">{{Supplier Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Destination</td>
              <td class="email-order-details-value">{{Install or Drop-Off Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking Number</td>
              <td class="email-order-details-value">{{Tracking Number}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You'll be notified once the installation is complete.
        </p>
      `,
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Installation Order Created": {
    subject: {
      AGENT: "️ Installation Order Submitted – #{{Order ID}}",
      SIGNAGE_FIRM: " New Installation Order Received – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " New Installation Order Received – #{{Order ID}}",
    },
    title: "Installation Order Created",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your installation order has been successfully submitted. The assigned supplier will review and schedule the work shortly.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Job Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Install Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You'll be notified when the supplier schedules and completes the installation.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          You've been assigned a new installation order from <strong>{{Agent Name}}</strong>. Please review the details and take appropriate action.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Products</td>
              <td class="email-order-details-value">{{List or summary}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Please update the status once scheduling or work is underway.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          You've been assigned a new installation order from <strong>{{Agent Name}}</strong>. Please review the details and take appropriate action.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Please update the status once scheduling or work is underway.
        </p>
      `,
    },
    cta: {
      SIGNAGE_FIRM: { text: " Access Installation Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Access Installation Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Installation Order Scheduled": {
    subject: " Installation Scheduled – Order #{{Order ID}}",
    title: "Installation Scheduled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage installation for Order #{{Order ID}} has been scheduled by {{Supplier Name}}.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Installation Schedule
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Location</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified once the installation is complete.
        </p>
      `,
        // <tr>
        //       <td class="email-order-details-field">Time Window</td>
        //       <td class="email-order-details-value">{{e.g., 9 AM – 12 PM}}</td>
        //     </tr>
    },
    cta: {
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Installation Order Completed": {
    subject: " Installation Completed – Order #{{Order ID}}",
    title: "Installation Completed",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage installation order #{{Order ID}} has been successfully completed by {{Supplier Name}}.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installed On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installed By</td>
              <td class="email-order-details-value">{{Supplier Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Photos Provided</td>
              <td class="email-order-details-value">{{Yes / No}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installer Notes</td>
              <td class="email-order-details-value">{{Sign placed near front gate}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      AGENT: { text: " View Completed Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Installation Order Canceled": {
    subject: {
      AGENT: " Your Installation Order Was Canceled – #{{Order ID}}",
      SIGNAGE_FIRM: " Installation Order Canceled – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " Installation Order Canceled – #{{Order ID}}",
    },
    title: "Installation Order Canceled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your installation order #{{Order ID}} has been canceled. Please review the cancellation details below:
        </p>
        
        <h3 class="email-heading-h3">
           Order Cancellation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{Supplier Name / System / Realesy Team}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{Duplicate request, Agent rescheduled, etc.}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was unintentional, you may place a new installation order.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          Installation Order #{{Order ID}} has been marked as canceled. Below is a record of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Cancellation Record
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{User / System}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{If supplied}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          The order has been removed from your active job list.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{First Name}},
        </h2>
        <p class="email-paragraph">
          Installation Order #{{Order ID}} has been marked as canceled. Below is a record of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Cancellation Record
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{User / System}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{If supplied}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          The order has been removed from your active job list.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " Submit New Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal Request Created": {
    subject: {
      AGENT: " Removal Request Submitted – #{{Order ID}}",
      SIGNAGE_FIRM: " New Removal Request – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " New Removal Request – #{{Order ID}}",
    },
    title: "Removal Request Created",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage removal request has been submitted successfully. The supplier will take action shortly.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Location for Removal}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested By</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified when the supplier schedules and completes the removal.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          You've received a new removal request from <strong>{{Agent Name}}</strong>. Please review and take action.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Request Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removal Address</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Priority</td>
              <td class="email-order-details-value">{{Standard / Urgent}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Please update the request as it progresses.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{First Name}},
        </h2>
        <p class="email-paragraph">
          You've received a new removal request from <strong>{{Agent Name}}</strong>. Please review and take action.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Request Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Full Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removal Address</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Priority</td>
              <td class="email-order-details-value">{{Standard / Urgent}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Please update the request as it progresses.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Access Removal Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Access Removal Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal Order Scheduled": {
    subject: " Your Signage Removal Is Scheduled – #{{Order ID}}",
    title: "Removal Scheduled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage removal for Order #{{Order ID}} has been scheduled by {{Supplier Name}}.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Scheduled Removal Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Time Window</td>
              <td class="email-order-details-value">{{e.g., 10 AM – 1 PM}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removal Location</td>
              <td class="email-order-details-value">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll receive a confirmation once the removal is complete.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal Order Completed": {
    subject: " Signage Removal Completed – Order #{{Order ID}}",
    title: "Removal Completed",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage removal for Order #{{Order ID}} has been successfully completed by {{Supplier Name}}.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Completion Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removed On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{Signage Company Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removal Address</td>
              <td class="email-order-details-value">{{Property Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Photos Provided</td>
              <td class="email-order-details-value">{{Yes / No}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value">{{Sign retrieved and returned to office}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the full details and any completion photos in your Realesy portal.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " View Completed Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal Order Canceled": {
    subject: {
      AGENT: " Your Signage Removal Was Canceled – Order #{{Order ID}}",
      SIGNAGE_FIRM: " Removal Order Canceled – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " Removal Order Canceled – #{{Order ID}}",
    },
    title: "Removal Canceled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order #{{Order ID}} has been canceled. Below are the details for your reference:
        </p>
        
        <h3 class="email-heading-h3">
           Cancellation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{Supplier / System / Admin}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Removal Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{Duplicate request, Access issue, etc.}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was not intentional or needs to be rescheduled, you may submit a new removal request.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          Removal Order #{{Order ID}} has been marked as canceled. Below is a record of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Cancellation Record
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{User / System}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Removal Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{If supplied}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          The order has been removed from your active job list.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{First Name}},
        </h2>
        <p class="email-paragraph">
          Removal Order #{{Order ID}} has been marked as canceled. Below is a record of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Cancellation Record
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value">{{User / System}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Removal Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{If supplied}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          The order has been removed from your active job list.
        </p>
      `,
    },
    cta: {
      AGENT: { text: " Submit New Removal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal Order Void": {
    subject: {
      AGENT: "️ Removal Order Voided – #{{Order ID}}",
      SIGNAGE_FIRM: " Removal Order Marked as Voided – #{{Order ID}}",
      SIGNAGE_TEAM_MEMBER: " Removal Order Marked as Voided – #{{Order ID}}",
    },
    title: "Removal Order Voided",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{Agent First Name}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order #{{Order ID}} has been voided by {{Supplier Name}}. This means the request will not be processed.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Void Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value">{{Supplier Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value">{{Property Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{e.g., Gate locked, Request duplicated}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was unintentional or conditions have changed, you can submit a new request.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{Supplier First Name}},
        </h2>
        <p class="email-paragraph">
          You have marked Removal Order #{{Order ID}} as Voided. The agent has been notified accordingly.
        </p>
        
        <h3 class="email-heading-h3">
           Void Confirmation
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Voided</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{Property Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{Optional internal notes}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{First Name}},
        </h2>
        <p class="email-paragraph">
          You have marked Removal Order #{{Order ID}} as Voided. The agent has been notified accordingly.
        </p>
        
        <h3 class="email-heading-h3">
           Void Confirmation
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">#{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{Agent Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Voided</td>
              <td class="email-order-details-value">{{MM/DD/YYYY}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value">{{Optional internal notes}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      AGENT: { text: " Submit New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Installation order edit": {
    subject: {
      AGENT: " Your Installation Order #{{orderNumber}} Was Updated",
      SIGNAGE_FIRM: "️ Edit Confirmed – Installation Order #{{orderNumber}}",
      SIGNAGE_TEAM_MEMBER: "️ Edit Confirmed – Installation Order #{{orderNumber}}",
    },
    title: "Installation Order Edit",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          {{supplierName}} has made updates to your installation order #{{orderNumber}}. Please find the updated details below:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{updatedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{changesMade}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the updated order in your portal and will be notified once the installation is completed.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your updates to Installation Order #{{orderNumber}} have been saved successfully. The agent has been notified.
        </p>
        
        <h3 class="email-heading-h3">
           Edit Confirmation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Edited</td>
              <td class="email-order-details-value-bold">{{dateEdited}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{changesMade}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your updates to Installation Order #{{orderNumber}} have been saved successfully. The agent has been notified.
        </p>
        
        <h3 class="email-heading-h3">
           Edit Confirmation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Edited</td>
              <td class="email-order-details-value-bold">{{dateEdited}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{changesMade}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      AGENT: { text: " View Updated Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Manage Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Manage Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "Agent Removal order edit": {
    subject: {
      AGENT: " Removal Order Updated – #{{orderNumber}}",
      SIGNAGE_FIRM: "️ Edit Confirmed – Removal Order #{{orderNumber}}",
      SIGNAGE_TEAM_MEMBER: "️ Edit Confirmed – Removal Order #{{orderNumber}}",
    },
    title: "Removal Order Edit",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order #{{orderNumber}} has been updated by {{supplierName}}. Please review the updated details below:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{updatedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{changesMade}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your changes to Removal Order #{{orderNumber}} have been successfully saved. The agent has been notified of the update.
        </p>
        
        <h3 class="email-heading-h3">
           Edit Confirmation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Edited</td>
              <td class="email-order-details-value-bold">{{dateEdited}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updates Made</td>
              <td class="email-order-details-value-bold">{{updatesMade}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your changes to Removal Order #{{orderNumber}} have been successfully saved. The agent has been notified of the update.
        </p>
        
        <h3 class="email-heading-h3">
           Edit Confirmation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Edited</td>
              <td class="email-order-details-value-bold">{{dateEdited}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updates Made</td>
              <td class="email-order-details-value-bold">{{updatesMade}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      AGENT: { text: " View Updated Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Manage Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Manage Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved.",
  },
};

